import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook to detect if an element is scrollable
 * @param {boolean} isActive - Whether to actively check for scrollability
 * @returns {Object} - { ref: React ref, isScrollable: boolean }
 */
const useScrollableDetection = (isActive = true) => {
  const ref = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    if (!isActive || !ref.current) return;

    const checkScrollability = () => {
      const element = ref.current;
      if (!element) return;

      // Check if content overflows
      const hasVerticalScroll = element.scrollHeight > element.clientHeight;
      const hasHorizontalScroll = element.scrollWidth > element.clientWidth;
      
      setIsScrollable(hasVerticalScroll || hasHorizontalScroll);
    };

    // Initial check
    checkScrollability();

    // Check on resize
    const resizeObserver = new ResizeObserver(checkScrollability);
    resizeObserver.observe(ref.current);

    // Check on content changes (using MutationObserver)
    const mutationObserver = new MutationObserver(checkScrollability);
    mutationObserver.observe(ref.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    // Cleanup
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [isActive]);

  return { ref, isScrollable };
};

export default useScrollableDetection;
